/**
 * The editable GSTR-3B: its figures, the arithmetic over them, and the checks.
 * @file src/web/features/admin/components/gstr3b-form.ts
 *
 * Pure — no React, no network. The screen holds one `Gstr3bForm`, prefilled
 * from the figures oddits computed, and every number on it can be changed.
 * What this file decides is what those numbers MEAN: 4C is A − B and is never
 * typed, the tax payable is 3.1(a) + 3.1(b) + 3.1(d), credit pays it in the
 * order section 49(5) and rule 88A set, and a form with an invalid field does
 * not become an upload payload.
 *
 * Nothing here talks to the portal. Building a payload is not sending one.
 *
 * @llm-rule WHEN: Anything on the GSTR-3B tab needs a figure that is derived from the form
 * @llm-rule AVOID: Re-deriving 4C or the set-off in a component — read it from here
 */
import { proposeSetOff, type Heads } from '../../../../api/features/gst/gst-setoff';

export type { Heads };

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Taxable value and the four heads, as GSTN names them. */
export interface Row { txval: number; iamt: number; camt: number; samt: number; csamt: number }
/** The four heads alone — table 4 has no taxable value. */
export interface Tax4 { iamt: number; camt: number; samt: number; csamt: number }
/** One line of 3.2: a place of supply and what was sold there. */
export interface PosRow { pos: string; txval: number; iamt: number }

export const ITC_AVL = ['IMPG', 'IMPS', 'ISRC', 'ISD', 'OTH'] as const;
export type ItcAvl = (typeof ITC_AVL)[number];
export const INTER_KINDS = ['unreg', 'comp', 'uin'] as const;
export type InterKind = (typeof INTER_KINDS)[number];

export interface Gstr3bForm {
  osup_det: Row;            // 3.1(a)
  osup_zero: Row;           // 3.1(b) — integrated tax and cess only
  osup_nil_exmp: number;    // 3.1(c) — value only
  isup_rev: Row;            // 3.1(d) — reverse charge
  osup_nongst: number;      // 3.1(e) — value only
  eco_sup: Row;             // 3.1.1(i)
  eco_reg_sup: number;      // 3.1.1(ii) — value only
  inter: Record<InterKind, PosRow[]>; // 3.2
  itc_avl: Record<ItcAvl, Tax4>;      // 4A(1)–(5)
  itc_rev: { RUL: Tax4; OTH: Tax4 };  // 4B(1), (2)
  itc_inelg: { RUL: Tax4; OTH: Tax4 }; // 4D(1), (2)
  inward: { GST: { inter: number; intra: number }; NONGST: { inter: number; intra: number } }; // 5
  interest: Tax4;                     // 5.1 interest
  lateFee: { camt: number; samt: number }; // 5.1 late fee — CGST and SGST only
}

const row = (): Row => ({ txval: 0, iamt: 0, camt: 0, samt: 0, csamt: 0 });
const tax4 = (): Tax4 => ({ iamt: 0, camt: 0, samt: 0, csamt: 0 });

export const emptyForm = (): Gstr3bForm => ({
  osup_det: row(), osup_zero: row(), osup_nil_exmp: 0, isup_rev: row(), osup_nongst: 0,
  eco_sup: row(), eco_reg_sup: 0,
  inter: { unreg: [], comp: [], uin: [] },
  itc_avl: { IMPG: tax4(), IMPS: tax4(), ISRC: tax4(), ISD: tax4(), OTH: tax4() },
  itc_rev: { RUL: tax4(), OTH: tax4() },
  itc_inelg: { RUL: tax4(), OTH: tax4() },
  inward: { GST: { inter: 0, intra: 0 }, NONGST: { inter: 0, intra: 0 } },
  interest: tax4(), lateFee: { camt: 0, samt: 0 },
});

const n = (v: unknown): number => {
  const x = typeof v === 'number' ? v : parseFloat(String(v ?? 0));
  return Number.isFinite(x) ? r2(x) : 0;
};
const toRow = (o: any): Row => ({ txval: n(o?.txval), iamt: n(o?.iamt), camt: n(o?.camt), samt: n(o?.samt), csamt: n(o?.csamt) });
const toTax4 = (o: any): Tax4 => ({ iamt: n(o?.iamt), camt: n(o?.camt), samt: n(o?.samt), csamt: n(o?.csamt) });
const toPos = (l: any): PosRow[] => (Array.isArray(l) ? l : []).map((x) => ({ pos: String(x?.pos ?? ''), txval: n(x?.txval), iamt: n(x?.iamt) }));

/**
 * The form, from the `portal` block oddits already computes for a period.
 * Absent pieces read as zero. 3.2 arrives as the single list oddits builds
 * for unregistered buyers (`intr_state_supplies`) or as GSTN's own
 * `inter_sup`; either fills the form.
 */
export function formFromPortal(p: any): Gstr3bForm {
  const f = emptyForm();
  if (!p) return f;
  const s = p.sup_details ?? {};
  f.osup_det = toRow(s.osup_det);
  f.osup_zero = { ...toRow(s.osup_zero), camt: 0, samt: 0 };
  f.osup_nil_exmp = n(s.osup_nil_exmp?.txval);
  f.isup_rev = toRow(s.isup_rev);
  f.osup_nongst = n(s.osup_nongst?.txval);
  f.eco_sup = toRow(p.eco_dtls?.eco_sup);
  f.eco_reg_sup = n(p.eco_dtls?.eco_reg_sup?.txval);
  if (p.inter_sup) {
    f.inter = { unreg: toPos(p.inter_sup.unreg_details), comp: toPos(p.inter_sup.comp_details), uin: toPos(p.inter_sup.uin_details) };
  } else {
    f.inter.unreg = toPos(p.intr_state_supplies);
  }
  const find = (list: any, ty: string) => (Array.isArray(list) ? list : []).find((x: any) => x?.ty === ty);
  for (const ty of ITC_AVL) f.itc_avl[ty] = toTax4(find(p.itc_elg?.itc_avl, ty));
  for (const ty of ['RUL', 'OTH'] as const) {
    f.itc_rev[ty] = toTax4(find(p.itc_elg?.itc_rev, ty));
    f.itc_inelg[ty] = toTax4(find(p.itc_elg?.itc_inelg, ty));
  }
  for (const ty of ['GST', 'NONGST'] as const) {
    const x = find(p.inward_sup?.isup_details, ty);
    f.inward[ty] = { inter: n(x?.inter), intra: n(x?.intra) };
  }
  f.interest = toTax4(p.intr_ltfee?.intr_details);
  f.lateFee = { camt: n(p.intr_ltfee?.ltfee_details?.camt), samt: n(p.intr_ltfee?.ltfee_details?.samt) };
  return f;
}

/** GSTN's 5.1, as the liability read returns it (`{igst,cgst,sgst,cess}` heads). */
export function withGstn51(form: Gstr3bForm, g: { interest?: Heads | null; lateFee?: Heads | null }): Gstr3bForm {
  const out = structuredClone(form);
  if (g.interest) out.interest = { iamt: n(g.interest.igst), camt: n(g.interest.cgst), samt: n(g.interest.sgst), csamt: n(g.interest.cess) };
  if (g.lateFee) out.lateFee = { camt: n(g.lateFee.cgst), samt: n(g.lateFee.sgst) };
  return out;
}

/* ── derived figures ───────────────────────────────────────────────────── */

const toHeads = (t: Tax4): Heads => ({ igst: t.iamt, cgst: t.camt, sgst: t.samt, cess: t.csamt });
const plus = (a: Heads, b: Heads): Heads => ({ igst: r2(a.igst + b.igst), cgst: r2(a.cgst + b.cgst), sgst: r2(a.sgst + b.sgst), cess: r2(a.cess + b.cess) });
const zeroHeads = (): Heads => ({ igst: 0, cgst: 0, sgst: 0, cess: 0 });

/** 4C = 4A − 4B, per head. Calculated; there is no field to type it into. */
export function netItc(f: Gstr3bForm): Tax4 {
  const sum = (pick: (t: Tax4) => number) =>
    r2(ITC_AVL.reduce((t, k) => t + pick(f.itc_avl[k]), 0) - (pick(f.itc_rev.RUL) + pick(f.itc_rev.OTH)));
  return { iamt: sum((t) => t.iamt), camt: sum((t) => t.camt), samt: sum((t) => t.samt), csamt: sum((t) => t.csamt) };
}

export interface Payable {
  /** 3.1(a) + 3.1(b) — tax on outward supplies; credit may pay this. */
  outward: Heads;
  /** 3.1(d) — reverse charge; section 49(4) makes this cash only. */
  reverseCharge: Heads;
  total: Heads;
}

/** Tax payable by head: 3.1(a) and the tax in 3.1(b), plus 3.1(d) kept apart. */
export function taxPayable(f: Gstr3bForm): Payable {
  const outward = plus(toHeads(f.osup_det), toHeads(f.osup_zero));
  const reverseCharge = toHeads(f.isup_rev);
  return { outward, reverseCharge, total: plus(outward, reverseCharge) };
}

export interface Ledgers { cash: Heads; itc: Heads }

export interface Payment {
  payable: Payable;
  /** Credit that stands against this return: 4C, plus the ledger when read. */
  credit: Heads;
  creditUsed: Heads;
  steps: { from: keyof Heads; to: keyof Heads; amount: number }[];
  /** Tax left after credit, per head — what only cash can now pay. Includes reverse charge. */
  payableAfterCredit: Heads;
  /** Cash for tax alone, before interest and late fee. */
  cashForTax: number;
  interest: Heads;
  lateFee: Heads;
  /** Everything the cash ledger must hold: tax after credit + interest + late fee. */
  cashRequired: number;
  cashRequiredByHead: Heads;
  /** Per head, cash short of that — null while the cash ledger is not read. */
  shortfall: Heads | null;
  /** True until the ledger balances come from the portal. */
  indicative: boolean;
}

/**
 * 6.1 — how credit pays the tax, and what is left for cash.
 *
 * Credit pays the outward tax in the order section 49(5) and rule 88A fix
 * (see gst-setoff.ts); reverse-charge tax, interest and late fee are cash.
 * The credit is 4C of this return plus the credit ledger when it has been
 * read from the portal — without the ledger the result is indicative.
 */
export function payment(f: Gstr3bForm, ledgers: Ledgers | null = null): Payment {
  const payable = taxPayable(f);
  const net = netItc(f);
  /* A negative 4C is a validation error, not a debit: never let it widen the liability. */
  const claimed: Heads = { igst: Math.max(0, net.iamt), cgst: Math.max(0, net.camt), sgst: Math.max(0, net.samt), cess: Math.max(0, net.csamt) };
  const credit = ledgers ? plus(ledgers.itc, claimed) : claimed;
  const interest = toHeads(f.interest);
  const lateFee: Heads = { igst: 0, cgst: f.lateFee.camt, sgst: f.lateFee.samt, cess: 0 };

  const s = proposeSetOff({ liability: payable.outward, credit, cash: ledgers?.cash ?? zeroHeads(), interest, lateFee });
  const payableAfterCredit = plus(s.cashForTax, payable.reverseCharge);
  const cashRequiredByHead = plus(plus(payableAfterCredit, interest), lateFee);
  const heads = ['igst', 'cgst', 'sgst', 'cess'] as const;
  let shortfall: Heads | null = null;
  if (ledgers) {
    shortfall = zeroHeads();
    for (const h of heads) shortfall[h] = r2(Math.max(0, cashRequiredByHead[h] - ledgers.cash[h]));
  }
  return {
    payable, credit, creditUsed: s.creditUsed, steps: s.credit,
    payableAfterCredit,
    cashForTax: r2(heads.reduce((t, h) => t + payableAfterCredit[h], 0)),
    interest, lateFee,
    cashRequired: r2(heads.reduce((t, h) => t + cashRequiredByHead[h], 0)),
    cashRequiredByHead, shortfall,
    indicative: !ledgers,
  };
}

/* ── checks ────────────────────────────────────────────────────────────── */

export interface Issue {
  level: 'error' | 'warn';
  /** Dotted path of the field, or a table name for a cross-table check. */
  field: string;
  message: string;
}

export interface CheckContext {
  /** 4A(5) as GSTR-2B supports it. Null when no 2B is loaded: the cap cannot be checked. */
  twoB?: Tax4 | null;
  /** Overrides what is inferred from 3.1 when the caller knows the exempt/taxable split. */
  exemptSplitKnown?: boolean;
}

const HEAD_LABEL: Record<string, string> = { txval: 'taxable value', iamt: 'IGST', camt: 'CGST', samt: 'SGST', csamt: 'Cess', inter: 'inter-State', intra: 'intra-State' };

/** Every number on the form, with the path it lives at. */
export function numericFields(f: Gstr3bForm): { path: string; label: string; value: number }[] {
  const out: { path: string; label: string; value: number }[] = [];
  const add = (path: string, label: string, value: number) => out.push({ path, label, value });
  const rowFields = (path: string, label: string, r: Row | Tax4) => {
    for (const [k, v] of Object.entries(r)) add(`${path}.${k}`, `${label} ${HEAD_LABEL[k] ?? k}`, v as number);
  };
  rowFields('osup_det', '3.1(a)', f.osup_det);
  rowFields('osup_zero', '3.1(b)', f.osup_zero);
  add('osup_nil_exmp', '3.1(c) taxable value', f.osup_nil_exmp);
  rowFields('isup_rev', '3.1(d)', f.isup_rev);
  add('osup_nongst', '3.1(e) taxable value', f.osup_nongst);
  rowFields('eco_sup', '3.1.1(i)', f.eco_sup);
  add('eco_reg_sup', '3.1.1(ii) taxable value', f.eco_reg_sup);
  for (const kind of INTER_KINDS) {
    f.inter[kind].forEach((r, i) => {
      add(`inter.${kind}.${i}.txval`, `3.2 ${kind} row ${i + 1} taxable value`, r.txval);
      add(`inter.${kind}.${i}.iamt`, `3.2 ${kind} row ${i + 1} IGST`, r.iamt);
    });
  }
  ITC_AVL.forEach((k, i) => rowFields(`itc_avl.${k}`, `4A(${i + 1})`, f.itc_avl[k]));
  (['RUL', 'OTH'] as const).forEach((k, i) => {
    rowFields(`itc_rev.${k}`, `4B(${i + 1})`, f.itc_rev[k]);
    rowFields(`itc_inelg.${k}`, `4D(${i + 1})`, f.itc_inelg[k]);
  });
  for (const ty of ['GST', 'NONGST'] as const) for (const k of ['inter', 'intra'] as const) add(`inward.${ty}.${k}`, `5 ${ty === 'GST' ? 'exempt/nil' : 'non-GST'} ${HEAD_LABEL[k]}`, f.inward[ty][k]);
  rowFields('interest', '5.1 interest', f.interest);
  add('lateFee.camt', '5.1 late fee CGST', f.lateFee.camt);
  add('lateFee.samt', '5.1 late fee SGST', f.lateFee.samt);
  return out;
}

const sum = (rows: PosRow[], k: 'txval' | 'iamt') => r2(rows.reduce((t, r) => t + (Number.isFinite(r[k]) ? r[k] : 0), 0));
const isZero4 = (t: Tax4) => t.iamt === 0 && t.camt === 0 && t.samt === 0 && t.csamt === 0;

/**
 * Everything wrong or doubtful about the form. An `error` stops the upload
 * payload being built; a `warn` is shown and does not.
 */
export function validate(f: Gstr3bForm, ctx: CheckContext = {}): Issue[] {
  const issues: Issue[] = [];

  for (const { path, label, value } of numericFields(f)) {
    if (!Number.isFinite(value)) issues.push({ level: 'error', field: path, message: `${label}: not a number.` });
    else if (value < 0) issues.push({ level: 'error', field: path, message: `${label}: cannot be negative.` });
    else if (Math.abs(value * 100 - Math.round(value * 100)) > 1e-6) issues.push({ level: 'error', field: path, message: `${label}: at most two decimals.` });
  }

  if (f.osup_zero.camt !== 0 || f.osup_zero.samt !== 0) {
    issues.push({ level: 'error', field: 'osup_zero', message: '3.1(b) zero-rated supplies carry integrated tax and cess only — central and state tax must be nil.' });
  }

  /* 3.2 is "of the supplies in 3.1(a) and 3.1.1(i)": it can be no larger than the two together. */
  const t32 = r2(INTER_KINDS.reduce((t, k) => t + sum(f.inter[k], 'txval'), 0));
  const cap = r2(f.osup_det.txval + f.eco_sup.txval);
  if (t32 > cap) {
    issues.push({ level: 'error', field: 'inter', message: `3.2 totals ₹${t32.toFixed(2)}, more than the ₹${cap.toFixed(2)} of 3.1(a) and 3.1.1(i) it is carved out of.` });
  }
  for (const kind of INTER_KINDS) {
    const seen = new Set<string>();
    f.inter[kind].forEach((r, i) => {
      if (!/^\d{2}$/.test(r.pos)) issues.push({ level: 'error', field: `inter.${kind}.${i}.pos`, message: `3.2 ${kind} row ${i + 1}: pick a place of supply.` });
      else if (seen.has(r.pos)) issues.push({ level: 'error', field: `inter.${kind}.${i}.pos`, message: `3.2 ${kind}: place of supply ${r.pos} appears twice — the portal takes one row per state.` });
      seen.add(r.pos);
    });
  }

  /* 4B cannot take back more than 4A gave. */
  const net = netItc(f);
  for (const [k, label] of [['iamt', 'IGST'], ['camt', 'CGST'], ['samt', 'SGST'], ['csamt', 'Cess']] as const) {
    if (net[k] < 0) issues.push({ level: 'error', field: 'itc_rev', message: `4B reverses more ${label} (₹${(-net[k]).toFixed(2)}) than 4A makes available.` });
  }

  /* Section 16(2)(aa): credit only as far as the supplier's filing shows it. */
  const oth = f.itc_avl.OTH;
  if (ctx.twoB) {
    for (const [k, label] of [['iamt', 'IGST'], ['camt', 'CGST'], ['samt', 'SGST'], ['csamt', 'Cess']] as const) {
      if (oth[k] > ctx.twoB[k] + 0.005) {
        issues.push({ level: 'warn', field: `itc_avl.OTH.${k}`, message: `4A(5) claims ₹${oth[k].toFixed(2)} ${label}; GSTR-2B supports ₹${ctx.twoB[k].toFixed(2)} (section 16(2)(aa)).` });
      }
    }
  } else if (!isZero4(oth)) {
    issues.push({ level: 'warn', field: 'itc_avl.OTH', message: '4A(5) cannot be checked against GSTR-2B — no 2B is loaded for this period.' });
  }

  /* Rule 42/43: exempt and taxable turnover side by side means some credit may need reversing. */
  const split = ctx.exemptSplitKnown ?? (r2(f.osup_nil_exmp + f.osup_nongst) > 0 && r2(f.osup_det.txval + f.osup_zero.txval) > 0);
  if (split && isZero4(f.itc_rev.RUL) && isZero4(f.itc_rev.OTH)) {
    issues.push({ level: 'warn', field: 'itc_rev', message: '4B is blank, but the period has both exempt and taxable supplies — rules 42 and 43 may require a reversal.' });
  }

  return issues;
}

export const errorsOf = (issues: Issue[]) => issues.filter((i) => i.level === 'error');

/* ── the upload payload ────────────────────────────────────────────────── */

const gstn = (r: Row) => ({ txval: r.txval, iamt: r.iamt, camt: r.camt, samt: r.samt, csamt: r.csamt });
const itcLine = (ty: string, t: Tax4) => ({ ty, ...t });

export type PayloadResult = { ok: true; payload: Record<string, unknown> } | { ok: false; issues: Issue[] };

/**
 * The form as GSTN's GSTR-3B body. Refuses — returning the errors — while any
 * field is invalid, so nothing malformed can reach an upload. It only BUILDS
 * a body: sending one is a separate, explicit step this file knows nothing of.
 */
export function buildUploadPayload(f: Gstr3bForm, head: { gstin: string; fp: string }, ctx: CheckContext = {}): PayloadResult {
  const errs = errorsOf(validate(f, ctx));
  if (errs.length) return { ok: false, issues: errs };
  const net = netItc(f);
  return {
    ok: true,
    payload: {
      gstin: head.gstin, ret_period: head.fp,
      sup_details: {
        osup_det: gstn(f.osup_det),
        osup_zero: gstn(f.osup_zero),
        osup_nil_exmp: { txval: f.osup_nil_exmp },
        isup_rev: gstn(f.isup_rev),
        osup_nongst: { txval: f.osup_nongst },
      },
      inter_sup: {
        unreg_details: f.inter.unreg.map((r) => ({ ...r })),
        comp_details: f.inter.comp.map((r) => ({ ...r })),
        uin_details: f.inter.uin.map((r) => ({ ...r })),
      },
      eco_dtls: { eco_sup: gstn(f.eco_sup), eco_reg_sup: { txval: f.eco_reg_sup } },
      inward_sup: { isup_details: [{ ty: 'GST', ...f.inward.GST }, { ty: 'NONGST', ...f.inward.NONGST }] },
      itc_elg: {
        itc_avl: ITC_AVL.map((ty) => itcLine(ty, f.itc_avl[ty])),
        itc_rev: [itcLine('RUL', f.itc_rev.RUL), itcLine('OTH', f.itc_rev.OTH)],
        itc_net: net,
        itc_inelg: [itcLine('RUL', f.itc_inelg.RUL), itcLine('OTH', f.itc_inelg.OTH)],
      },
      intr_ltfee: {
        intr_details: { ...f.interest },
        ltfee_details: { camt: f.lateFee.camt, samt: f.lateFee.samt },
      },
    },
  };
}
